
import { Component, Prop } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { mixins } from 'vue-class-component';
import { sum } from 'lodash';
import BaseChartWrapper from '@/ui/components/devices/charts/charts/BaseChartWrapper.vue';
import { periodConfigurations, Periods } from '@/ui/components/devices/charts/charts/ChartUtils';
import { hasSystem } from '@/utils/utilsFunctions';
import { mixedEnergyChartScaling } from '@/utils/scalingConstants';
import { kiloWattHourUnit } from '@/utils/unit/unitConstants';
import {
  EnergyCircleType,
  producers,
} from '@/types/energyVisualisation/EnergyCircleType';
import EnergyComponent
  from '@/ui/components/devices/components/EnergyParts/EnergyVisualisation/utils/EnergyComponent';
import { IDevice } from '@/types/devices.types';
import { ChartData, ChartOption } from '@/types/chart.types';

@Component({
  methods: {
    kiloWattHourUnit() {
      return kiloWattHourUnit;
    },
  },
  components: { BaseChartWrapper },
})
export default class EnergyFlowConsumptionChart extends mixins(EnergyComponent) {
  @Prop({ default: null }) deviceData!: IDevice;
  @Prop({ default: '' }) mpcId!: string;
  @Prop() settingsWrapperHeight!: number;
  @Prop({ default: false }) isMpc!: boolean;

  @Action('mpc/fetchDataByPeriodMpc') fetchDataByPeriodMpc!: (
    { id, start, end, period }: { id: string; start: number; end: number; period: string }
  ) => Promise<any>;
  @Action('devices/fetchDataByPeriod') fetchDataByPeriod!: (
    { id, start, end, period }: { id: string; start: number; end: number; period: string }
  ) => Promise<any>;

  scores: Record<string, number> = {};
  scoresForDataExport: Record<string, {value: number; unit: string}> = {};
  translation = {
    Eigenverbrauchsanteil: this.$t(
      'mlModel.EMS.charts.chartDifference.eigenverbrauchsanteil',
    ).toString(),
    SelfSufficientScore: this.$t(
      'mlModel.EMS.charts.chartDifference.selfSufficientScore',
    ).toString(),
    TotalConsumption: this.$t('mlModel.EMS.charts.chartDifference.totalConsumption').toString(),
  };
  units = {
    SelfSufficientScore: '%',
    Eigenverbrauchsanteil: '%',
  };

  period = Periods.DAY;
  start = periodConfigurations[this.period].period().start;
  end = periodConfigurations[this.period].period().end;
  energyResponse: any = null; // TODO: check and add type here (possibly it EnergyDataByPeriodResponse)

  systemCount(system: EnergyCircleType) {
    return this.getSystemCount(this.deviceData, system);
  }

  get chartData(): ChartData {
    const generatorVariables = this.getVariableMappings(this.deviceData, EnergyCircleType.generator);
    const chpVariables = this.getVariableMappings(this.deviceData, EnergyCircleType.chp);
    const pvVariables = this.getVariableMappings(this.deviceData, EnergyCircleType.pv);
    const productionSum = [...pvVariables, ...generatorVariables, ...chpVariables].join('+');

    const batteryVariableSum = this.getVariableMappings(this.deviceData, EnergyCircleType.battery).join('+');
    const gridVariableSum = this.getVariableMappings(this.deviceData, EnergyCircleType.grid).join('+');
    const batterySocMapping = this.getVariableMappings(this.deviceData, EnergyCircleType.battery, 'soc').join('+');

    return {
      data: {
        chartOptions: [
          ...(this.isSystemPresent(this.deviceData, EnergyCircleType.pv) ? [{
            calculation: {
              // ['avg', ...] array with length of total variables pv, battery, grid
              aggregations: new Array(
                this.systemCount(EnergyCircleType.pv)
                  + this.systemCount(EnergyCircleType.generator)
                  + this.systemCount(EnergyCircleType.chp)
                  + this.systemCount(EnergyCircleType.battery)
                  + this.systemCount(EnergyCircleType.grid),
              ).fill('avg'),
              // sum(pv_power) + sum(bprod_power) + sum(gprod_power),
              expression: `max(max(${productionSum}, 0) + min(${batteryVariableSum || 0},0) + min(${gridVariableSum || 0},0), 0)`,
            },
            name: this.$t('mlModel.EMS.charts.chartDifference.directConsumption').toString(),
            scaling: mixedEnergyChartScaling,
            seriesType: 'Calculation',
            type: 'line',
            unit: 'kW',
          } as ChartOption] : []),
          ...(this.isSystemPresent(this.deviceData, EnergyCircleType.battery) ? [{
            calculation: {
              aggregations: new Array(this.systemCount(EnergyCircleType.battery)).fill('avg'),
              expression: `max(${batteryVariableSum}, 0)`,
            },
            name: this.$t('mlModel.EMS.charts.chartDifference.batteryToConsumer').toString(),
            scaling: mixedEnergyChartScaling,
            seriesType: 'Calculation',
            type: 'line',
            unit: 'kW',
          } as ChartOption,
          {
            calculation: {
              aggregations: ['avg', 'avg'],
              expression: `min(${batteryVariableSum}, 0) * (-1)`,
            },
            name: this.$t('mlModel.EMS.charts.chartDifference.productionBattery').toString(),
            scaling: mixedEnergyChartScaling,
            seriesType: 'Calculation',
            type: 'line',
            unit: 'kW',
          } as ChartOption] : []),
          ...(this.isSystemPresent(this.deviceData, EnergyCircleType.grid) ? [{
            calculation: {
              aggregations: new Array(this.systemCount(EnergyCircleType.grid)).fill('avg'),
              expression: `max(${gridVariableSum}, 0)`,
            },
            name: this.$t('mlModel.EMS.charts.chartDifference.gridToConsumer').toString(),
            scaling: mixedEnergyChartScaling,
            seriesType: 'Calculation',
            type: 'line',
            unit: 'kW',
          } as ChartOption] : []),
          ...(this.isSystemPresent(this.deviceData, EnergyCircleType.pv) ? [{
            calculation: {
              aggregations: [...pvVariables, ...generatorVariables, ...chpVariables].fill('avg'),
              expression: `max(${productionSum}, 0)`,
            },
            name: this.$t('mlModel.EMS.charts.chartDifference.production').toString(),
            scaling: mixedEnergyChartScaling,
            seriesType: 'Calculation',
            type: 'line',
            unit: 'kW',
          } as ChartOption] : []),
          ...(this.isSystemPresent(this.deviceData, EnergyCircleType.battery) ? [{
            calculation: {
              aggregations: new Array(this.systemCount(EnergyCircleType.battery)).fill('avg'),
              expression: `${batterySocMapping}`,
            },
            name: this.$t('mlModel.EMS.charts.chartTimeTableConsumer.soc').toString(),
            scaling: { min: 0, max: 100 },
            seriesType: 'Calculation',
            type: 'line',
            unit: '%',
          } as ChartOption] : []),
        ],
      },
    };
  }

  roundNumber(num: number) {
    return Number(num.toFixed(2));
  }

  systemFilterSum(system: any, containedString: string) {
    let sum = 0;
    if (!this.energyResponse.energies[system]) return sum;
    Object.entries(this.energyResponse.energies[system]).forEach((system: any) => {
      if (system[0].includes(containedString)) {
        sum += system[1];
      }
    });
    return sum;
  }

  get productionSum(): number {
    const producerValues = producers.flatMap((t: EnergyCircleType) => this.getSystemKeys(this.deviceData, t)
      .map((key: string) => this.energyResponse?.energies?.[t]?.[key] ?? 0));
    return sum(producerValues);
  }

  async chartRangeChange(params: any) {
    this.start = params.start;
    this.end = params.end;
    this.period = params.period;
    const fetchEnergyDataFunction = this.isMpc ? this.fetchDataByPeriodMpc : this.fetchDataByPeriod;
    this.energyResponse = await fetchEnergyDataFunction({
      id: this.mpcId.replaceAll('_', '-'),
      start: this.start,
      end: this.end,
      period: this.period,
    });
    const batteryToConsumer = `${this.$t('mlModel.EMS.charts.chartDifference.batteryToConsumer')}`;
    const gridToConsumer = `${this.$t('mlModel.EMS.charts.chartDifference.gridToConsumer')}`;
    const directConsumption = `${this.$t('mlModel.EMS.charts.chartDifference.directConsumption')}`;
    const batteryConsumerSum = Math.max(this.systemFilterSum('battery', 'to_consumer'), 0);
    const batteryProductionSum = Math.max(this.systemFilterSum('battery', 'from_production'), 0);
    const gridConsumerSum = Math.max(this.systemFilterSum('grid', 'to_consumer'), 0);
    const gridProductionSum = Math.max(this.systemFilterSum('grid', 'from_production'), 0);
    const directConsumptionValue = Object.keys(this.energyResponse.calculations).includes(
      'direct_consumption',
    )
      ? this.energyResponse.calculations.direct_consumption
      : Math.max(this.productionSum - batteryProductionSum - gridProductionSum, 0);

    const scoresForDataExport: any = {};

    if (this.systemCount(EnergyCircleType.pv)) {
      this.scores[directConsumption] = this.roundNumber(directConsumptionValue);
      this.scoresForDataExport[directConsumption] = { value: this.roundNumber(directConsumptionValue), unit: 'kWh' };
    }

    if (this.systemCount(EnergyCircleType.battery)) {
      this.scores[batteryToConsumer] = batteryConsumerSum;
      this.scoresForDataExport[batteryToConsumer] = { value: batteryConsumerSum, unit: 'kWh' };
    }

    if (this.systemCount(EnergyCircleType.grid)) {
      this.scores[gridToConsumer] = this.roundNumber(gridConsumerSum);
      this.scoresForDataExport[gridToConsumer] = { value: this.roundNumber(gridConsumerSum), unit: 'kWh' };
    }
    this.scores = {
      ...this.scores,
      TotalConsumption: this.roundNumber(this.energyResponse.calculations.total_consumption),
      ...(this.systemCount(EnergyCircleType.pv) && this.systemCount(EnergyCircleType.grid) ? {
        SelfSufficientScore: this.roundNumber(this.energyResponse.calculations.autarchy_score),
        Eigenverbrauchsanteil: this.roundNumber(
          Object.keys(this.energyResponse.calculations).includes('self_consumption_score')
            ? this.energyResponse.calculations.self_consumption_score
            : this.energyResponse.calculations.self_sufficient_score,
        ),
      } : {}),
    };
    this.scoresForDataExport.TotalConsumption = { value: this.scores.TotalConsumption, unit: 'kWh' };
    this.scoresForDataExport.SelfSufficientScore = { value: this.scores.SelfSufficientScore, unit: '%' };
    this.scoresForDataExport.Eigenverbrauchsanteil = { value: this.scores.Eigenverbrauchsanteil, unit: '%' };
  }

  async created() {
    const fetchEnergyDataFunction = this.isMpc ? this.fetchDataByPeriodMpc : this.fetchDataByPeriod;
    this.energyResponse = await fetchEnergyDataFunction({
      id: this.mpcId.replaceAll('_', '-'),
      start: this.start,
      end: this.end,
      period: this.period,
    });
  }
}
